import { Box, Paper } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useState } from "react"

import { InvoicesFilterChips } from "./components/filters/InvoicesFilterChips"
import { InvoicesFilterDrawer } from "./components/filters/InvoicesFilterDrawer"
import { InvoiceAs400ReconciliationDialog } from "./components/InvoiceAs400ReconciliationDialog"
import { InvoiceCancelDialog } from "./components/InvoiceCancelDialog"
import { InvoiceFormDialog } from "./components/InvoiceFormDialog"
import { InvoicesTable } from "./components/InvoicesTable"
import { InvoiceVoidDialog } from "./components/InvoiceVoidDialog"
import { INVOICES_SEARCH_PLACEHOLDER } from "./constants"
import { InvoiceRowAction } from "./constants/rowActions"
import { useGetInvoice } from "./hooks/useGetInvoice"
import { useInvoiceList } from "./hooks/useInvoiceList"
import type { InvoicesSearchParams } from "./schemas"
import { resolveInvoiceFormDialogMode } from "./utils"

import { Button } from "@/components/common/Button"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import { PermissionActionType } from "@/constants/permissions"
import type { Invoice, InvoiceAs400SyncKind } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"

type KebabAction =
  | { mode: Exclude<InvoiceRowAction, InvoiceRowAction.As400Reconciliation>; invoiceId: string }
  | { mode: InvoiceRowAction.As400Reconciliation; invoiceId: string; syncKind: InvoiceAs400SyncKind }

export const InvoicesContainer: FC = () => {
  const [isAddFormDialogOpen, setIsAddFormDialogOpen] = useState(false)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [activeKebabAction, setActiveKebabAction] = useState<KebabAction | null>(null)

  const { hasPermissions } = usePermissionManager()
  const canCreateInvoice = hasPermissions([PermissionActionType.ACCOUNTS_PAYABLE_MAINTENANCE])

  const {
    invoices,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  } = useInvoiceList()

  const handleEdit = useCallback((row: Invoice) => {
    setActiveKebabAction({ mode: InvoiceRowAction.Edit, invoiceId: row.invoiceId })
  }, [])

  const handleView = useCallback((row: Invoice) => {
    setActiveKebabAction({ mode: InvoiceRowAction.View, invoiceId: row.invoiceId })
  }, [])

  const handleCancel = useCallback((row: Invoice) => {
    setActiveKebabAction({ mode: InvoiceRowAction.Cancel, invoiceId: row.invoiceId })
  }, [])

  const handleVoid = useCallback((row: Invoice) => {
    setActiveKebabAction({ mode: InvoiceRowAction.Void, invoiceId: row.invoiceId })
  }, [])

  const handleConfirmAs400Reconciliation = useCallback((row: Invoice, syncKind: InvoiceAs400SyncKind) => {
    setActiveKebabAction({ mode: InvoiceRowAction.As400Reconciliation, invoiceId: row.invoiceId, syncKind })
  }, [])

  const handleKebabActionClose = useCallback(() => {
    setActiveKebabAction(null)
  }, [])

  const {
    invoice: activeInvoice,
    isFetching: isFetchingInvoice,
    refetch: refetchActiveInvoice,
  } = useGetInvoice({
    invoiceId:
      activeKebabAction?.mode === InvoiceRowAction.As400Reconciliation ? undefined : activeKebabAction?.invoiceId,
  })

  const editOrViewMode =
    activeKebabAction?.mode === InvoiceRowAction.Edit || activeKebabAction?.mode === InvoiceRowAction.View
      ? activeKebabAction.mode
      : undefined

  return (
    <Box className='flex size-full flex-col'>
      <Paper elevation={0} className='flex min-h-0 grow flex-col gap-4 overflow-hidden bg-white'>
        <TableHeaderActions<InvoicesSearchParams>
          currentSearch={searchParams}
          placeholder={INVOICES_SEARCH_PLACEHOLDER}
          navigateTo='/app/accounting/accounts-payable'
          testIdPrefix='invoices'
        >
          <Button
            appearance='outlined'
            startIcon={<FilterAltOutlined />}
            onClick={() => setIsFilterDrawerOpen(true)}
            rootElementName='button'
            data-testid='invoices.filter-button'
          >
            Filter
          </Button>

          <Box className='flex items-center gap-2'>
            {canCreateInvoice && (
              <Button
                appearance='contained'
                startIcon={<AddOutlined />}
                onClick={() => setIsAddFormDialogOpen(true)}
                rootElementName='button'
                data-testid='invoices.add-invoice-button'
              >
                Add Invoice
              </Button>
            )}
          </Box>
        </TableHeaderActions>

        <Box className='px-4'>
          <InvoicesFilterChips searchParams={searchParams} onRemove={handleFilterRemove} />
        </Box>

        <Box className='flex flex-1 flex-col overflow-y-auto'>
          <InvoicesTable
            data={invoices}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            loading={isFetching}
            error={error ? (error.message ?? "Failed to load invoices") : null}
            scrollHandler={handleScroll}
            tableContainerRef={tableContainerRef}
            onEdit={handleEdit}
            onView={handleView}
            onCancel={handleCancel}
            onVoid={handleVoid}
            onConfirmAs400Reconciliation={handleConfirmAs400Reconciliation}
          />
        </Box>
      </Paper>

      {isAddFormDialogOpen && canCreateInvoice && (
        <InvoiceFormDialog mode='create' onClose={() => setIsAddFormDialogOpen(false)} onSuccess={refetch} />
      )}

      {editOrViewMode && activeInvoice && !isFetchingInvoice && (
        <InvoiceFormDialog
          mode={resolveInvoiceFormDialogMode(activeInvoice, editOrViewMode)}
          invoice={activeInvoice}
          onClose={handleKebabActionClose}
          onSuccess={() => {
            handleKebabActionClose()
            refetch()
          }}
          onRefresh={refetchActiveInvoice}
        />
      )}

      {activeKebabAction?.mode === InvoiceRowAction.Cancel && activeInvoice && !isFetchingInvoice && (
        <InvoiceCancelDialog
          invoice={activeInvoice}
          onClose={handleKebabActionClose}
          onSuccess={() => {
            handleKebabActionClose()
            refetch()
          }}
          onRefresh={refetchActiveInvoice}
        />
      )}

      {activeKebabAction?.mode === InvoiceRowAction.Void && activeInvoice && !isFetchingInvoice && (
        <InvoiceVoidDialog
          invoice={activeInvoice}
          onClose={handleKebabActionClose}
          onSuccess={() => {
            handleKebabActionClose()
            refetch()
          }}
          onRefresh={refetchActiveInvoice}
        />
      )}

      {activeKebabAction?.mode === InvoiceRowAction.As400Reconciliation && (
        <InvoiceAs400ReconciliationDialog
          invoiceId={activeKebabAction.invoiceId}
          syncKind={activeKebabAction.syncKind}
          onClose={handleKebabActionClose}
          onSuccess={() => {
            handleKebabActionClose()
            refetch()
          }}
          onFailure={() => {
            handleKebabActionClose()
            refetch()
          }}
        />
      )}

      {isFilterDrawerOpen && (
        <InvoicesFilterDrawer
          searchParams={searchParams}
          onClose={() => setIsFilterDrawerOpen(false)}
          onChange={handleFilterChange}
          onClear={handleFilterClear}
        />
      )}
    </Box>
  )
}
